import { enums } from '@maxsystems/client'

import { currency as currencyFilter, number as numberFilter, date as dateFilter } from '~/ui/vue/filters'

/**
 * Converts datetime to date-only string to handle UTC values, this prevents
 * the date from being displayed as the previous day in the user's timezone
 */
const datetimeToDateWithFormat = format => ({ value: dateTime }) => {
  if (!dateTime) return '-'
  const date = new Date(dateTime).toISOString().split('T')[0].replace(/-/g, '/')
  return dateFilter(date, format)
}
const datetimeWithFormat = format => ({ value }) => value ? dateFilter(value, format) : '-'

const datetimeToDateFormatter = datetimeToDateWithFormat('M/D/YYYY')
datetimeToDateFormatter.withFormat = datetimeToDateWithFormat

const datetimeFormatter = datetimeWithFormat('M/D/YYYY hh:mm A')
datetimeFormatter.withFormat = datetimeWithFormat

const booleanWithValues = (trueValue = 'Yes', falseValue = 'No') => ({ value }) => value ? trueValue : falseValue

const booleanFormatter = booleanWithValues()
booleanFormatter.withValues = booleanWithValues

export const boolean = booleanFormatter
export const commaSpace = ({ value }) => value.join(', ')
export const currency = ({ value }) => {
  if (isNaN(parseInt(value))) return '-'
  return currencyFilter(value) || '-'
}
export const date = datetimeToDateFormatter
export const datetime = datetimeFormatter
export const normalizeLabel = ({ value }) => enums.normalizeLabel(value)
export const number = ({ value }) => numberFilter(value)

/**
 * Expands a formatter to return a default value if the provided value is falsy or nullish, depending on the options.
 * @param {Object} options
 * @param {string} options.defaultValue The default value to return
 * @param {(params: { value: any }) => any} options.formatter The formatter to expand
 * @param {'falsy' | 'nullish'} options.defaultOn The type of condition to display the default value.
 * @param {boolean} options.emptyArrayAsFalsy The option to treat empty arrays as falsy values
 * @returns The modified formatter
 */
export function withDefault ({
  defaultOn = 'falsy',
  defaultValue = '-',
  emptyArrayAsFalsy = true,
  formatter = params => params.value
} = {}) {
  if (process.env.NODE_ENV !== 'production') {
    if (!['falsy', 'nullish'].includes(defaultOn)) {
      throw new Error(`Invalid defaultOn option: ${defaultOn}`)
    }
  }

  const shouldShowDefaultValue = params => {
    if (defaultOn === 'falsy') {
      if (Array.isArray(params.value) && emptyArrayAsFalsy) return !params.value.length
      return !params.value
    }

    return params.value === null || params.value === undefined
  }

  return params => shouldShowDefaultValue(params)
    ? defaultValue
    : formatter(params)
}

/**
 * Expands a formatter to return a dash (`-`) if the provided value is falsy
 * @param {(params: { value: any }) => any} formatter The formatter to expand
 * @returns The modified formatter
 */
export const withDashForFalsy = formatter => withDefault({ formatter })
